import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

/* ─── Inline icons (slate-700 strokes, 1.6 weight) ───────────── */
const Icon: React.FC<React.PropsWithChildren<{ size?: number; className?: string }>> = ({
  size = 20,
  className,
  children,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

const LogoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

const ArrowIcon: React.FC = () => (
  <Icon size={18}>
    <path d="M5 12h14" />
    <path d="m13 5 7 7-7 7" />
  </Icon>
);

const ShieldCheckIcon = () => (
  <Icon>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    <path d="m9 12 2 2 4-4" />
  </Icon>
);

const LockIcon = () => (
  <Icon>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Icon>
);

const EyeOffIcon = () => (
  <Icon>
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </Icon>
);

const MaskIcon = () => (
  <Icon>
    <path d="M12 3c4 0 8 1 8 5v4c0 5-3.5 9-8 9s-8-4-8-9V8c0-4 4-5 8-5Z" />
    <path d="M8 11h.01M16 11h.01" />
    <path d="M8 15c1.5 1 6.5 1 8 0" />
  </Icon>
);

const FamilyIcon = () => (
  <Icon>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const ScanIcon = () => (
  <Icon>
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <path d="M7 12h10" />
  </Icon>
);

const SparkleIcon = () => (
  <Icon>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    <path d="m5.6 5.6 2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </Icon>
);

const TimelineIcon = () => (
  <Icon>
    <circle cx="6" cy="6" r="2" />
    <circle cx="6" cy="18" r="2" />
    <path d="M6 8v8" />
    <path d="M12 6h9" />
    <path d="M12 18h9" />
    <path d="M12 12h6" />
  </Icon>
);

const ShareIcon = () => (
  <Icon>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
  </Icon>
);

const SearchIcon = () => (
  <Icon>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </Icon>
);

const ChatIcon = () => (
  <Icon>
    <path d="M21 12a8 8 0 0 1-11.4 7.2L4 21l1.8-5.6A8 8 0 1 1 21 12Z" />
  </Icon>
);

const CameraIcon = () => (
  <Icon>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
    <circle cx="12" cy="13" r="4" />
  </Icon>
);

const MailIcon = () => (
  <Icon size={18}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 7 9-7" />
  </Icon>
);

/* ─── CSS-only "member dossier" mockup ─────────────────────── */
const DossierMockup: React.FC = () => (
  <div className="lp-mock" role="img" aria-label="Previzualizare dosar medical">
    <div className="lp-mock-glow" aria-hidden />

    <div className="lp-mock-card lp-mock-card--main">
      <div className="lp-mock-search">
        <SearchIcon />
        <span>Caută în dosar… </span>
        <span className="lp-mock-search-caret" />
      </div>

      <div className="lp-mock-member">
        <div className="lp-mock-avatar" aria-hidden>AP</div>
        <div className="lp-mock-member-meta">
          <div className="lp-mock-member-name">Andrei Popescu</div>
          <div className="lp-mock-member-sub">Băiat · 8 ani · 22 documente</div>
        </div>
        <span className="lp-mock-status">
          <span className="lp-mock-status-dot" />
          activ
        </span>
      </div>

      <div className="lp-mock-tabs" aria-hidden>
        <span className="lp-mock-tab lp-mock-tab--active">Sumar</span>
        <span className="lp-mock-tab">Documente</span>
        <span className="lp-mock-tab">Cronologie</span>
        <span className="lp-mock-tab">Critic</span>
      </div>

      <div className="lp-mock-docs">
        <div className="lp-mock-doc">
          <div className="lp-mock-doc-thumb lp-mock-doc-thumb--a" aria-hidden>
            <span /><span /><span /><span /><span />
          </div>
          <div className="lp-mock-doc-meta">
            <div className="lp-mock-doc-title">Analize de sânge</div>
            <div className="lp-mock-doc-sub">Synevo · 12 mai 2026</div>
          </div>
          <span className="lp-mock-pill lp-mock-pill--success">analize</span>
        </div>
        <div className="lp-mock-doc">
          <div className="lp-mock-doc-thumb lp-mock-doc-thumb--b" aria-hidden>
            <span /><span /><span /><span /><span />
          </div>
          <div className="lp-mock-doc-meta">
            <div className="lp-mock-doc-title">Scrisoare medicală</div>
            <div className="lp-mock-doc-sub">Dr. Ionescu · 28 apr</div>
          </div>
          <span className="lp-mock-pill lp-mock-pill--info">scrisoare</span>
        </div>
        <div className="lp-mock-doc">
          <div className="lp-mock-doc-thumb lp-mock-doc-thumb--c" aria-hidden>
            <span /><span /><span /><span /><span />
          </div>
          <div className="lp-mock-doc-meta">
            <div className="lp-mock-doc-title">Radiografie torace</div>
            <div className="lp-mock-doc-sub">Pulmocenter · 3 apr</div>
          </div>
          <span className="lp-mock-pill lp-mock-pill--warn">imagistic</span>
        </div>
      </div>

      <div className="lp-mock-timeline" aria-hidden>
        <div className="lp-mock-timeline-track" />
        <span className="lp-mock-timeline-node" style={{ left: '8%' }} />
        <span className="lp-mock-timeline-node lp-mock-timeline-node--accent" style={{ left: '28%' }} />
        <span className="lp-mock-timeline-node" style={{ left: '46%' }} />
        <span className="lp-mock-timeline-node lp-mock-timeline-node--accent" style={{ left: '68%' }} />
        <span className="lp-mock-timeline-node" style={{ left: '88%' }} />
        <div className="lp-mock-timeline-labels">
          <span>ian</span><span>feb</span><span>mar</span><span>apr</span><span>mai</span>
        </div>
      </div>
    </div>

    <div className="lp-mock-chip lp-mock-chip--ai">
      <SparkleIcon />
      <div>
        <div className="lp-mock-chip-title">Sumar AI</div>
        <div className="lp-mock-chip-sub">Valori în limite normale</div>
      </div>
    </div>

    <div className="lp-mock-chip lp-mock-chip--upload">
      <CameraIcon />
      <div>
        <div className="lp-mock-chip-title">Document nou</div>
        <div className="lp-mock-chip-sub">OCR + clasificare</div>
      </div>
      <span className="lp-mock-chip-progress">
        <span />
      </span>
    </div>

    <div className="lp-mock-chip lp-mock-chip--lock" aria-hidden>
      <LockIcon />
      <span>E2E</span>
    </div>
  </div>
);

/* ─── Main component ─────────────────────────────────────── */
const LandingPage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const yearRef = useRef(new Date().getFullYear());

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('.lp-reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lp-reveal--in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -64px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  const closeMenu = () => setMobileNavOpen(false);

  return (
    <div className="lp">
      {/* ─── Header ───────────────────────────── */}
      <header className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <Link to="/" className="lp-brand" onClick={closeMenu}>
            <LogoIcon />
            <span>eDosar<strong>Medical</strong></span>
          </Link>

          <nav className="lp-nav-links" aria-label="Navigare site">
            <a href="#features">Funcții</a>
            <a href="#how">Cum funcționează</a>
            <a href="#privacy">Securitate</a>
          </nav>

          <div className="lp-nav-cta">
            <Link to="/login" className="lp-btn lp-btn--ghost lp-btn--sm">
              Autentificare
            </Link>
            <Link to="/register" className="lp-btn lp-btn--primary lp-btn--sm">
              Începe gratuit
            </Link>
          </div>

          <button
            type="button"
            className={`lp-nav-burger${mobileNavOpen ? ' lp-nav-burger--open' : ''}`}
            aria-label={mobileNavOpen ? 'Închide meniul' : 'Deschide meniul'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <span /><span /><span />
          </button>
        </div>

        <div className={`lp-nav-mobile${mobileNavOpen ? ' lp-nav-mobile--open' : ''}`} aria-hidden={!mobileNavOpen}>
          <a href="#features" onClick={closeMenu}>Funcții</a>
          <a href="#how" onClick={closeMenu}>Cum funcționează</a>
          <a href="#privacy" onClick={closeMenu}>Securitate</a>
          <hr />
          <Link to="/login" onClick={closeMenu} className="lp-btn lp-btn--ghost">
            Autentificare
          </Link>
          <Link to="/register" onClick={closeMenu} className="lp-btn lp-btn--primary">
            Începe gratuit
          </Link>
        </div>
      </header>

      <main>
        {/* ─── Hero ─────────────────────────────── */}
        <section className="lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-hero-bg" aria-hidden>
            <div className="lp-hero-grid" />
            <div className="lp-hero-glow lp-hero-glow--a" />
            <div className="lp-hero-glow lp-hero-glow--b" />
            <div className="lp-hero-glow lp-hero-glow--c" />
          </div>

          <div className="lp-hero-inner">
            <div className="lp-hero-copy">
              <p className="lp-hero-badge">
                <span className="lp-hero-badge-dot" />
                Criptat end-to-end · pentru familii din România
              </p>
              <h1 id="lp-hero-title" className="lp-hero-title">
                Dosarul medical al familiei,
                <span className="lp-hero-title-grad"> într-un singur loc sigur.</span>
              </h1>
              <p className="lp-hero-lead">
                Strânge analize, rețete și scrisori pentru tine, copii sau părinți într-un dosar privat.
                Aplicația citește documentele, le sortează cronologic și răspunde la întrebări — fără să-ți
                părăsească niciodată cheia.
              </p>
              <div className="lp-hero-ctas">
                <Link to="/register" className="lp-btn lp-btn--primary lp-btn--lg">
                  Începe gratuit
                  <ArrowIcon />
                </Link>
                <Link to="/login" className="lp-btn lp-btn--soft lp-btn--lg">
                  Autentificare
                </Link>
              </div>
              <ul className="lp-hero-trust" aria-label="Garanții">
                <li>
                  <ShieldCheckIcon /> Criptare E2E
                </li>
                <li>
                  <LockIcon /> Doar tu ai cheia
                </li>
                <li>
                  <EyeOffIcon /> Fără tracking
                </li>
              </ul>
            </div>

            <div className="lp-hero-visual" aria-hidden>
              <DossierMockup />
            </div>
          </div>
        </section>

        {/* ─── Reassurance strip ────────────────── */}
        <section className="lp-strip" aria-label="De ce să ai încredere">
          <div className="lp-strip-inner">
            <div className="lp-strip-item lp-reveal">
              <ShieldCheckIcon />
              <div>
                <strong>Criptare end-to-end</strong>
                <span>Decriptare doar pe dispozitivul tău</span>
              </div>
            </div>
            <div className="lp-strip-item lp-reveal" style={{ transitionDelay: '60ms' }}>
              <LockIcon />
              <div>
                <strong>Cheia rămâne la tine</strong>
                <span>Fără parolă, nici noi nu putem citi dosarul</span>
              </div>
            </div>
            <div className="lp-strip-item lp-reveal" style={{ transitionDelay: '120ms' }}>
              <MaskIcon />
              <div>
                <strong>Anonimizare automată</strong>
                <span>Datele personale sunt mascate în procesare</span>
              </div>
            </div>
            <div className="lp-strip-item lp-reveal" style={{ transitionDelay: '180ms' }}>
              <EyeOffIcon />
              <div>
                <strong>Fără publicitate, fără tracking</strong>
                <span>Nu vindem și nu monetizăm dosarul tău</span>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Features ─────────────────────────── */}
        <section className="lp-section" id="features">
          <div className="lp-section-inner">
            <header className="lp-section-head lp-reveal">
              <p className="lp-kicker">Funcții</p>
              <h2 className="lp-section-title">
                Tot ce-ți trebuie ca să nu mai cauți acea hârtie din 2022.
              </h2>
              <p className="lp-section-lead">
                Încarci, fotografiezi sau dictezi — restul se întâmplă automat. Fără tabele Excel, fără foldere
                pe desktop, fără să te încurci în jargon medical.
              </p>
            </header>

            <div className="lp-features">
              {[
                {
                  icon: <CameraIcon />,
                  title: 'Fotografie sau import',
                  body: 'Adaugă documente direct din camera telefonului sau încarcă PDF-uri și poze trimise pe e-mail.',
                  accent: 'a',
                },
                {
                  icon: <ScanIcon />,
                  title: 'OCR + auto-clasificare',
                  body: 'Aplicația „citește" scrisul de pe hârtie și pune fiecare document la categoria potrivită — analize, scrisori, imagistică.',
                  accent: 'b',
                },
                {
                  icon: <TimelineIcon />,
                  title: 'Cronologie pe membru',
                  body: 'Vezi pe o singură axă tot istoricul medical al unei persoane — analize lângă tratamente, în ordinea în care s-au întâmplat.',
                  accent: 'c',
                },
                {
                  icon: <FamilyIcon />,
                  title: 'Familie + acces partajat',
                  body: 'Adaugi membri în familie sau partajezi un singur dosar cu medicul curant — fără să dai parola contului.',
                  accent: 'd',
                },
                {
                  icon: <ChatIcon />,
                  title: 'Întrebări în română',
                  body: 'Întrebi „ce zicea analiza din martie?" și primești un răspuns scris pe înțelesul tău, doar din dosarul vostru.',
                  accent: 'e',
                },
                {
                  icon: <SparkleIcon />,
                  title: 'Rezumate AI',
                  body: 'Sumare clare pentru fiecare document și pentru întreg istoricul — utile când mergi la un medic nou.',
                  accent: 'f',
                },
              ].map((f, i) => (
                <article
                  key={f.title}
                  className={`lp-feature lp-feature--${f.accent} lp-reveal`}
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <span className="lp-feature-icon">{f.icon}</span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ─── How it works ─────────────────────── */}
        <section className="lp-section lp-section--alt" id="how">
          <div className="lp-section-inner">
            <header className="lp-section-head lp-reveal">
              <p className="lp-kicker">În trei pași</p>
              <h2 className="lp-section-title">De la teancuri de hârtii la un dosar limpede.</h2>
              <p className="lp-section-lead">
                Nu trebuie să fii bun cu calculatorul. Dacă știi să trimiți o poză pe WhatsApp, poți și aici.
              </p>
            </header>

            <ol className="lp-steps">
              <li className="lp-step lp-reveal">
                <span className="lp-step-num">1</span>
                <h3>Adaugi familia</h3>
                <p>Pui pe rând persoanele pentru care vrei să păstrezi documentele — copilul, partenerul, mama.</p>
              </li>
              <li className="lp-step lp-reveal" style={{ transitionDelay: '100ms' }}>
                <span className="lp-step-num">2</span>
                <h3>Încarci documentele</h3>
                <p>Faci poze cu telefonul sau încarci PDF-uri. Aplicația le citește și le pune în ordine.</p>
              </li>
              <li className="lp-step lp-reveal" style={{ transitionDelay: '200ms' }}>
                <span className="lp-step-num">3</span>
                <h3>Primești dosarul complet</h3>
                <p>Cauți, citești sau întrebi în română — totul rămâne organizat pe membru, pe categorie, pe dată.</p>
              </li>
            </ol>
          </div>
        </section>

        {/* ─── Privacy band ─────────────────────── */}
        <section className="lp-privacy" id="privacy">
          <div className="lp-privacy-bg" aria-hidden>
            <div className="lp-privacy-glow lp-privacy-glow--a" />
            <div className="lp-privacy-glow lp-privacy-glow--b" />
            <div className="lp-privacy-grid" />
          </div>
          <div className="lp-privacy-inner lp-reveal">
            <p className="lp-kicker lp-kicker--light">Confidențialitate</p>
            <h2 className="lp-privacy-title">
              Dosarul tău, <span className="lp-privacy-title-grad">cu cheia în buzunarul tău.</span>
            </h2>
            <p className="lp-privacy-lead">
              Fiecare document este criptat în browserul tău înainte să plece de pe dispozitiv. Cheia derivă
              din parola ta — nu o vedem, nu o stocăm, nu o putem reseta în locul tău. Asta înseamnă că
              dosarul medical al familiei rămâne ilizibil pentru oricine în afară de tine — chiar și pentru
              cei care administrează serverul.
            </p>

            <div className="lp-privacy-grid-cards">
              <div className="lp-privacy-card">
                <LockIcon />
                <h4>Criptare în browser</h4>
                <p>AES-256 + cheie derivată local din parola ta — nimic nu pleacă în clar.</p>
              </div>
              <div className="lp-privacy-card">
                <ShareIcon />
                <h4>Partajare granulară</h4>
                <p>Dai acces controlat pe membru sau pe întreaga familie. Retragerea e instant.</p>
              </div>
              <div className="lp-privacy-card">
                <MaskIcon />
                <h4>Anonimizare automată</h4>
                <p>Datele personale (nume, CNP, adresă) sunt mascate înainte de procesarea AI.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Final CTA ────────────────────────── */}
        <section className="lp-cta">
          <div className="lp-cta-bg" aria-hidden />
          <div className="lp-cta-inner lp-reveal">
            <h2>Începe să-ți organizezi dosarul medical astăzi.</h2>
            <p>
              Îți faci contul în câteva secunde și încarci primul document când ai timp. Datele tale rămân
              criptate — chiar și după ce închizi laptopul.
            </p>
            <div className="lp-cta-actions">
              <Link to="/register" className="lp-btn lp-btn--invert lp-btn--lg">
                Vreau dosarul meu digital
                <ArrowIcon />
              </Link>
              <Link to="/login" className="lp-btn lp-btn--ghost-light lp-btn--lg">
                Am deja cont
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ─── Footer ─────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <Link to="/" className="lp-brand lp-brand--footer">
              <LogoIcon />
              <span>eDosar<strong>Medical</strong></span>
            </Link>
            <p>Dosarul medical al familiei tale, simplu, cald și criptat.</p>
          </div>
          <div className="lp-footer-cols">
            <div className="lp-footer-col">
              <h4>Produs</h4>
              <a href="#features">Funcții</a>
              <a href="#how">Cum funcționează</a>
              <a href="#privacy">Securitate</a>
            </div>
            <div className="lp-footer-col">
              <h4>Cont</h4>
              <Link to="/login">Autentificare</Link>
              <Link to="/register">Înregistrare</Link>
            </div>
            <div className="lp-footer-col">
              <h4>Contact</h4>
              <a href="mailto:contact@edosarmedical.ro" className="lp-footer-link-icon">
                <MailIcon /> Scrie-ne
              </a>
              <a href="#privacy">Confidențialitate</a>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© {yearRef.current} eDosarMedical · Toate drepturile rezervate</span>
          <span className="lp-footer-tag">
            <span className="lp-footer-tag-dot" />
            v1 · în România, pentru familii
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
